import { observable, makeObservable, toJS } from 'mobx'
import {
  TILE_CONTENT,
  TILE_SIZES,
  MATERIAL_TYPES,
  FRAME_COLORS,
  MATTING,
  FILTERS,
  type TILE_SIZES as TileSizesType,
  type MATERIAL_TYPES as MaterialTypesType,
  type FRAME_COLORS as FrameColorsType,
  type MATTING as MattingType,
  type FILTERS as FiltersType,
  type MatColorKey,
  MAT_COLORS,
} from '@mixtiles/web-backend-shared'
import { BaseTileStore } from './BaseTileStore'
import {
  type CustomCenterpieceProps,
  type StylingFields,
} from '../TilesStore.types'
import type { PhotoBankStore } from '../../photoBankStore/PhotoBankStore'
import type { CenterpieceTileData } from './tileSchemas'
import { type TilePersistenceDisposition } from './BaseTileStore'
import { type TilePatchMode } from '../TilePatch'

const SAVE_CENTERPIECE_KEYS = [
  'highResUrl',
  'lowResUrl',
  'centerpieceTitle',
  'customCenterpieceProps',
  'isCustomized',
  'width',
  'height',
]
const CENTERPIECE_PATCHABLE_KEYS_WHITELIST = new Set([
  ...SAVE_CENTERPIECE_KEYS,
  'localImageDimensions',
  'localUrl',
  'thumbUrl',
])

export class CenterpieceTileStore extends BaseTileStore {
  @observable accessor centerpieceTitle: string

  @observable.deep accessor customCenterpieceProps:
    | CustomCenterpieceProps
    | undefined

  @observable accessor isCustomized: boolean | undefined

  @observable accessor highResUrl: string

  @observable accessor lowResUrl: string

  @observable accessor width: number

  @observable accessor height: number

  @observable accessor localImageDimensions:
    | { width: number; height: number }
    | undefined

  @observable accessor localUrl: string | undefined

  @observable accessor thumbUrl: string | undefined

  constructor(data: CenterpieceTileData, photoBankStore: PhotoBankStore) {
    const centerpieceData = {
      ...data,
      customStyle: {
        frameColor: FRAME_COLORS.NONE,
        matting: MATTING.NONE,
        filter: FILTERS.NONE,
        mattingColor: MAT_COLORS.NONE as MatColorKey,
        lastFramedColor: FRAME_COLORS.NONE,
      },
      tileSize: TILE_SIZES.RECTANGLE_25X8,
      materialType: MATERIAL_TYPES.FRAMELESS,
    }
    super(centerpieceData, photoBankStore)

    this.width = data.width
    this.height = data.height
    this.centerpieceTitle = data.centerpieceTitle ?? ''
    this.customCenterpieceProps = data.customCenterpieceProps
    this.isCustomized = data.isCustomized
    this.highResUrl = data.highResUrl ?? ''
    this.lowResUrl = data.lowResUrl ?? ''
    this.localImageDimensions = data.localImageDimensions
    this.localUrl = data.localUrl
    this.thumbUrl = data.thumbUrl
    makeObservable(this)
  }

  get tileContent(): TILE_CONTENT {
    return TILE_CONTENT.CENTERPIECE
  }

  override getPhotoUids(): string[] {
    return []
  }

  override getPersistenceState(): TilePersistenceDisposition {
    return 'ready'
  }

  override toTileData(): CenterpieceTileData {
    return {
      ...this.baseTileData(),
      tileContent: TILE_CONTENT.CENTERPIECE,
      width: this.width,
      height: this.height,
      centerpieceTitle: this.centerpieceTitle,
      customCenterpieceProps: toJS(this.customCenterpieceProps),
      isCustomized: this.isCustomized,
      highResUrl: this.highResUrl,
      lowResUrl: this.lowResUrl,
      localImageDimensions: toJS(this.localImageDimensions),
      localUrl: this.localUrl,
      thumbUrl: this.thumbUrl,
    }
  }

  override get persistenceKeys(): string[] {
    return super.persistenceKeys.concat(SAVE_CENTERPIECE_KEYS)
  }

  protected override getPatchableKeysWhitelist(
    mode: TilePatchMode
  ): Set<string> {
    return new Set([
      ...super.getPatchableKeysWhitelist(mode),
      ...CENTERPIECE_PATCHABLE_KEYS_WHITELIST,
    ])
  }

  override applyMaterial(_materialType: MaterialTypesType): void {}

  override updateSize(_tileSize: TileSizesType): void {}

  override applyFullStyling(_style: Partial<StylingFields>): void {}

  override updateFrameColor(_color: FrameColorsType): void {}

  override updateMatting(_matting: MattingType): void {}

  override updateFilter(_filter: FiltersType): void {}

  override updateMattingColor(_color: MatColorKey): void {}
}
